<div class="br-table-filter-bar" role="search" aria-label="Table filters">
  <ng-container *ngFor="let field of visibleFields; trackBy: trackField">
    <span class="br-table-filter-rule"
          [class.br-table-filter-rule--removable]="isRemovable(field)">
      <button type="button" *ngIf="kindOf(field) === 'choice'; else ruleTrigger"
              class="br-table-filter-trigger"
              [class.br-table-filter-trigger--active]="hasValue(field.id)"
              [brMenuTriggerFor]="filterMenu"
              [attr.aria-label]="'Filter by ' + field.label">
        <span class="df-trigger-label">{{ labelFor(field) }}</span>
        <br-icon aria-hidden="true">keyboard_arrow_down</br-icon>
      </button>

      <ng-template #ruleTrigger>
        <button type="button" class="br-table-filter-trigger"
                [class.br-table-filter-trigger--active]="hasValue(field.id)"
                [brPopoverFor]="filterRule" [brPopoverLabel]="'Filter by ' + field.label"
                [attr.aria-label]="'Filter by ' + field.label">
          <span class="df-trigger-label">{{ labelFor(field) }}</span>
          <br-icon aria-hidden="true">keyboard_arrow_down</br-icon>
        </button>
      </ng-template>

      <!-- Only an unpinned rule can leave the bar. A pinned one is deselected
           through its own "All" option, which keeps the control in place. -->
      <button type="button"
              *ngIf="isRemovable(field)"
              class="br-table-filter-remove"
              (click)="remove(field.id)"
              [attr.aria-label]="'Remove the ' + field.label + ' filter'">
        <br-icon aria-hidden="true">close</br-icon>
      </button>
    </span>

    <ng-template #filterMenu>
      <br-menu [attr.aria-label]="'Filter by ' + field.label">
        <button brMenuItem [label]="field.allLabel || 'All'"
                [current]="valueFor(field.id) === null"
                [icon]="valueFor(field.id) === null ? 'check' : ''"
                (triggered)="setValue(field.id, null)"></button>
        <button brMenuItem *ngFor="let option of field.options; trackBy: trackOption"
                [label]="option.label" [current]="valueFor(field.id) === option.value"
                [icon]="valueFor(field.id) === option.value ? 'check' : ''"
                (triggered)="setValue(field.id, option.value)"></button>
      </br-menu>
    </ng-template>

    <ng-template #filterRule>
      <div class="df-rule">
        <div class="df-ops" role="group" [attr.aria-label]="field.label + ' comparison'">
          <button type="button" class="df-op"
                  *ngFor="let op of opsFor(field)"
                  [class.df-op--on]="opFor(field) === op.id"
                  [attr.aria-pressed]="opFor(field) === op.id"
                  (click)="setOp(field, op.id)">{{ op.label }}</button>
        </div>

        <!-- `change`, not `input`: one query when the value is settled, not
             one per keystroke against a database-paged list. -->
        <input class="df-value" #valueBox
               [type]="kindOf(field) === 'number' ? 'number'
                       : kindOf(field) === 'date' ? 'date' : 'text'"
               [value]="textFor(field)"
               [attr.aria-label]="field.label + ' value'"
               (change)="setRule(field, opFor(field), valueBox.value)"
               (keyup.enter)="setRule(field, opFor(field), valueBox.value)" />

        <button type="button" class="df-op df-clear"
                (click)="setValue(field.id, null); valueBox.value = ''">Clear</button>
      </div>
    </ng-template>
  </ng-container>

  <!-- Hidden on a page that declares no unpinned columns, so a bar of pinned
       filters looks exactly as it did before. -->
  <ng-container *ngIf="panelFields.length">
    <!-- Named, not a plus sign. A "+" on a bar of dropdowns is only obvious to
         somebody who already knows this bar filters; the word says what the
         thing behind it is, and the count says whether any of it is on. -->
    <button type="button"
            class="df-panel-trigger br-interactive"
            [class.br-interactive--selected]="panelActiveCount > 0 || panelTrigger.isOpen()"
            [class.df-panel-trigger--active]="panelActiveCount > 0"
            #panelTrigger="brPopoverTrigger"
            [brPopoverFor]="filterPanel" brPopoverLabel="More filters"
            (popoverOpened)="openPanel()"
            (popoverClosed)="closePanel()"
            aria-label="More filters">
      <br-icon aria-hidden="true">tune</br-icon>
      <span>Filters</span>
      <span class="df-panel-count" *ngIf="panelActiveCount > 0">{{ panelActiveCount }}</span>
      <br-icon class="df-panel-caret" aria-hidden="true">
        {{ panelTrigger.isOpen() ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
      </br-icon>
    </button>

    <ng-template #filterPanel>
      <div class="df-panel">
        <div class="df-panel-head">Filters</div>

        <div class="df-panel-body">
          <section class="df-group" *ngFor="let field of panelFields; trackBy: trackField"
                   [attr.data-field]="field.id">
            <h4 class="df-group-title" [id]="'df-group-' + field.id">{{ field.label }}</h4>

            <select class="br-form-select br-form-select--sm" *ngIf="kindOf(field) === 'choice'"
                    [attr.aria-labelledby]="'df-group-' + field.id"
                    (change)="setPanelChoice(field, $any($event.target).value)">
              <option value="" [selected]="panelValueFor(field.id) === null">
                {{ field.allLabel || 'All' }}
              </option>
              <option *ngFor="let option of field.options; trackBy: trackOption"
                      [value]="option.value" [selected]="panelValueFor(field.id) === option.value">
                {{ option.label }}
              </option>
            </select>

            <!-- Everything else needs both halves of the question. -->
            <div class="df-rule df-rule--panel" *ngIf="kindOf(field) !== 'choice'">
              <select class="br-form-select br-form-select--sm"
                      [attr.aria-label]="field.label + ' comparison'"
                      (change)="setPanelOp(field, $any($event.target).value)">
                <option *ngFor="let op of opsFor(field)" [value]="op.id"
                        [selected]="panelOpFor(field) === op.id">{{ op.label }}</option>
              </select>

              <input class="br-form-control br-form-control--sm" #panelBox
                     [type]="kindOf(field) === 'number' ? 'number'
                             : kindOf(field) === 'date' ? 'date' : 'text'"
                     [value]="panelTextFor(field)"
                     [attr.aria-label]="field.label + ' value'"
                     (change)="setPanelRule(field, panelOpFor(field), panelBox.value)" />
            </div>
          </section>
        </div>

        <!-- The two ways out, at the foot where they are looked for: undo
             everything, or ask the question. Nothing above this line has
             touched the rows yet. -->
        <div class="df-panel-foot">
          <button type="button" class="df-panel-reset"
                  [disabled]="!panelHasAnyValue"
                  (click)="resetAll()">Reset all filters</button>
          <button type="button" class="br-btn br-btn--primary br-btn--sm"
                  (click)="applyPanel(); panelTrigger.close()">Apply</button>
        </div>
      </div>
    </ng-template>
  </ng-container>
</div>
